import { useTranslation } from 'react-i18next';

import { CustomTypography } from '@/shared/ui/Typography';

import {
  type AiAnalyticsDataStatus,
  type AiAnalyticsReport,
  formatCurrencyValue,
  formatDurationMs,
  formatMetricValue,
  formatNumber,
  formatPercentValue,
} from './analytics-api.js';
import {
  CompactMetricCard,
  CompactMetricHint,
  CompactMetricLabel,
  CompactMetricValue,
  DataState,
  DataTable,
  DenseMetricGrid,
  EmptyText,
  InlineMetaList,
  ListGrid,
  ListItem,
  MetaLabel,
  MetaRow,
  MetaValue,
  Panel,
  PanelHeader,
  PanelHeaderMeta,
  RangeErrorState,
  ReadinessGrid,
  ReadinessItem,
  SectionGrid,
  StatusPill,
  TableScroller,
} from './analytics-page.styles.js';

type AiAnalyticsReportPanelProps = {
  report: AiAnalyticsReport | undefined;
  isLoading: boolean;
  isError: boolean;
  missingCompany: boolean;
  rangeTooWide: boolean;
};

type MetricItem = {
  key: string;
  labelKey: string;
  value: string;
  hintKey?: string;
};

const dataStatusKeys: Array<keyof AiAnalyticsReport['data_status']> = [
  'activity_logs',
  'delayed_deliveries',
  'module_scope',
  'outbound_policy',
  'chat_metrics',
  'crm_attribution',
  'channel_health',
];

const formatCode = (value: string) => value.replace(/_/g, ' ');

const formatDateTime = (value: string) => {
  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return value;

  return new Intl.DateTimeFormat('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

const translateDynamic = (
  t: ReturnType<typeof useTranslation>['t'],
  key: string,
  fallback: string
) => {
  const translated = t(key);

  return translated === key ? fallback : translated;
};

const renderMetricGrid = (items: MetricItem[]) => (
  <DenseMetricGrid>
    {items.map((item) => (
      <CompactMetricCard key={item.key}>
        <CompactMetricLabel variant="body2">{item.labelKey}</CompactMetricLabel>
        <CompactMetricValue component="div" variant="h6">
          {item.value}
        </CompactMetricValue>
        {item.hintKey ? (
          <CompactMetricHint variant="body2">{item.hintKey}</CompactMetricHint>
        ) : null}
      </CompactMetricCard>
    ))}
  </DenseMetricGrid>
);

export const AiAnalyticsReportPanel = ({
  report,
  isLoading,
  isError,
  missingCompany,
  rangeTooWide,
}: AiAnalyticsReportPanelProps) => {
  const { t } = useTranslation();

  if (missingCompany) {
    return (
      <DataState>
        <CustomTypography component="h2" variant="h6">
          {t('common:analyticsPage.ai.state.missingCompanyTitle')}
        </CustomTypography>
        <CustomTypography variant="body1">
          {t('common:analyticsPage.ai.state.missingCompanyDescription')}
        </CustomTypography>
      </DataState>
    );
  }

  if (rangeTooWide) {
    return (
      <RangeErrorState>
        <CustomTypography component="h2" variant="h6">
          {t('common:analyticsPage.ai.state.rangeTooWideTitle')}
        </CustomTypography>
        <CustomTypography variant="body1">
          {t('common:analyticsPage.ai.state.rangeTooWideDescription')}
        </CustomTypography>
      </RangeErrorState>
    );
  }

  if (isLoading) {
    return (
      <DataState>
        <CustomTypography component="h2" variant="h6">
          {t('common:analyticsPage.ai.state.loading')}
        </CustomTypography>
      </DataState>
    );
  }

  if (isError || !report) {
    return (
      <RangeErrorState>
        <CustomTypography component="h2" variant="h6">
          {t('common:analyticsPage.ai.state.errorTitle')}
        </CustomTypography>
        <CustomTypography variant="body1">
          {t('common:analyticsPage.ai.state.errorDescription')}
        </CustomTypography>
      </RangeErrorState>
    );
  }

  const activityMetrics: MetricItem[] = [
    {
      key: 'draftReplies',
      labelKey: t('common:analyticsPage.ai.metrics.draftReplies'),
      value: formatNumber(report.activity.draft_replies),
    },
    {
      key: 'campaignDrafts',
      labelKey: t('common:analyticsPage.ai.metrics.campaignDrafts'),
      value: formatNumber(report.activity.campaign_drafts),
    },
    {
      key: 'autonomousReplies',
      labelKey: t('common:analyticsPage.ai.metrics.autonomousReplies'),
      value: formatNumber(report.activity.autonomous_replies_sent),
    },
    {
      key: 'handoffPauses',
      labelKey: t('common:analyticsPage.ai.metrics.handoffPauses'),
      value: formatNumber(report.activity.handoff_pauses),
    },
    {
      key: 'safeAutomationBlocks',
      labelKey: t('common:analyticsPage.ai.metrics.safeAutomationBlocks'),
      value: formatNumber(report.activity.safe_automation_blocks),
    },
    {
      key: 'failedOperations',
      labelKey: t('common:analyticsPage.ai.metrics.failedOperations'),
      value: formatNumber(report.activity.failed_operations),
    },
  ];

  const deliveryMetrics: MetricItem[] = [
    {
      key: 'delayedPending',
      labelKey: t('common:analyticsPage.ai.metrics.delayedPending'),
      value: formatNumber(report.deliveries.delayed_pending),
    },
    {
      key: 'delayedProcessing',
      labelKey: t('common:analyticsPage.ai.metrics.delayedProcessing'),
      value: formatNumber(report.deliveries.delayed_processing),
    },
    {
      key: 'delayedSent',
      labelKey: t('common:analyticsPage.ai.metrics.delayedSent'),
      value: formatNumber(report.deliveries.delayed_sent),
    },
    {
      key: 'delayedBlocked',
      labelKey: t('common:analyticsPage.ai.metrics.delayedBlocked'),
      value: formatNumber(report.deliveries.delayed_blocked),
    },
    {
      key: 'delayedFailed',
      labelKey: t('common:analyticsPage.ai.metrics.delayedFailed'),
      value: formatNumber(report.deliveries.delayed_failed),
    },
    {
      key: 'deliveryRetries',
      labelKey: t('common:analyticsPage.ai.metrics.deliveryRetries'),
      value: formatNumber(report.deliveries.retry_count),
    },
  ];

  const providerMetrics: MetricItem[] = [
    {
      key: 'avgLatency',
      labelKey: t('common:analyticsPage.ai.metrics.avgLatency'),
      value: formatDurationMs(report.provider.avg_latency_ms),
    },
    {
      key: 'p95Latency',
      labelKey: t('common:analyticsPage.ai.metrics.p95Latency'),
      value: formatDurationMs(report.provider.p95_latency_ms),
    },
    {
      key: 'tokens',
      labelKey: t('common:analyticsPage.ai.metrics.tokens'),
      value: formatNumber(report.provider.total_tokens),
    },
    {
      key: 'providerCost',
      labelKey: t('common:analyticsPage.ai.metrics.providerCost'),
      value: formatCurrencyValue(report.provider.estimated_cost_usd, 'USD'),
    },
    {
      key: 'providerRetries',
      labelKey: t('common:analyticsPage.ai.metrics.providerRetries'),
      value: formatNumber(report.provider.retry_count),
    },
  ];

  const operationalMetrics: MetricItem[] = [
    {
      key: 'firstResponse',
      labelKey: t('common:analyticsPage.ai.metrics.firstResponse'),
      value: report.operational.first_response_minutes === null
        ? '—'
        : `${formatNumber(report.operational.first_response_minutes)} ${t(
            'common:analyticsPage.units.minutes'
          )}`,
      hintKey: t('common:analyticsPage.ai.hints.joinedMetric'),
    },
    {
      key: 'aiContainment',
      labelKey: t('common:analyticsPage.ai.metrics.aiContainment'),
      value: formatPercentValue(report.operational.ai_containment_rate),
    },
    {
      key: 'missedDialogs',
      labelKey: t('common:analyticsPage.ai.metrics.missedDialogs'),
      value: formatNumber(report.operational.missed_dialogs),
    },
    {
      key: 'lostLeads',
      labelKey: t('common:analyticsPage.ai.metrics.lostLeads'),
      value: formatNumber(report.operational.lost_leads_count),
      hintKey: t('common:analyticsPage.ai.hints.joinedMetric'),
    },
    {
      key: 'answeredDialogs',
      labelKey: t('common:analyticsPage.ai.metrics.answeredDialogs'),
      value: formatNumber(report.operational.answered_dialogs),
    },
    {
      key: 'inboundDialogs',
      labelKey: t('common:analyticsPage.ai.metrics.inboundDialogs'),
      value: formatNumber(report.operational.inbound_dialogs),
    },
  ];

  return (
    <>
      <Panel>
        <PanelHeader>
          <div>
            <CustomTypography component="h2" variant="h6">
              {t('common:analyticsPage.ai.sections.dataReadiness')}
            </CustomTypography>
            <PanelHeaderMeta variant="body2">
              {t('common:analyticsPage.ai.dataReadinessDescription')}
            </PanelHeaderMeta>
          </div>
          <StatusPill tone={report.crm_impact.attribution_ready ? 'ready' : 'partial'}>
            {report.crm_impact.attribution_ready
              ? t('common:analyticsPage.ai.crm.attributionReady')
              : t('common:analyticsPage.ai.crm.attributionPartial')}
          </StatusPill>
        </PanelHeader>

        <ReadinessGrid>
          {dataStatusKeys.map((key) => {
            const status = report.data_status[key] as AiAnalyticsDataStatus;

            return (
              <ReadinessItem key={key}>
                <CustomTypography variant="body2">
                  {t(`common:analyticsPage.ai.dataSources.${key}`)}
                </CustomTypography>
                <StatusPill tone={status}>
                  {t(`common:analyticsPage.ai.dataStatus.${status}`)}
                </StatusPill>
              </ReadinessItem>
            );
          })}
        </ReadinessGrid>
      </Panel>

      <SectionGrid>
        <Panel>
          <PanelHeader>
            <CustomTypography component="h2" variant="h6">
              {t('common:analyticsPage.ai.sections.moduleScope')}
            </CustomTypography>
          </PanelHeader>
          <InlineMetaList>
            <MetaRow>
              <MetaLabel>{t('common:analyticsPage.ai.module.status')}</MetaLabel>
              <MetaValue>
                {t(
                  `common:analyticsPage.ai.module.analyticsStatus.${report.module_scope.analytics_status}`
                )}
              </MetaValue>
            </MetaRow>
            <MetaRow>
              <MetaLabel>{t('common:analyticsPage.ai.module.billing')}</MetaLabel>
              <MetaValue>
                {t(
                  `common:analyticsPage.ai.module.billingState.${report.module_scope.billing_state}`
                )}
              </MetaValue>
            </MetaRow>
            <MetaRow>
              <MetaLabel>{t('common:analyticsPage.ai.module.setup')}</MetaLabel>
              <MetaValue>
                {t(
                  `common:analyticsPage.ai.module.setupState.${report.module_scope.setup_state}`
                )}
              </MetaValue>
            </MetaRow>
            <MetaRow>
              <MetaLabel>{t('common:analyticsPage.ai.module.channels')}</MetaLabel>
              <MetaValue>
                {t('common:analyticsPage.ai.module.channelCount', {
                  count: report.module_scope.scoped_channel_ids.length,
                })}
              </MetaValue>
            </MetaRow>
            <MetaRow>
              <MetaLabel>{t('common:analyticsPage.ai.module.capabilities')}</MetaLabel>
              <MetaValue>
                {report.module_scope.capabilities.length > 0
                  ? report.module_scope.capabilities.map(formatCode).join(', ')
                  : '—'}
              </MetaValue>
            </MetaRow>
          </InlineMetaList>
        </Panel>

        <Panel>
          <PanelHeader>
            <CustomTypography component="h2" variant="h6">
              {t('common:analyticsPage.ai.sections.crmImpact')}
            </CustomTypography>
          </PanelHeader>
          <InlineMetaList>
            <MetaRow>
              <MetaLabel>{t('common:analyticsPage.ai.crm.attributed')}</MetaLabel>
              <MetaValue>{formatNumber(report.crm_impact.attributed_dialogs)}</MetaValue>
            </MetaRow>
            <MetaRow>
              <MetaLabel>{t('common:analyticsPage.ai.crm.unattributed')}</MetaLabel>
              <MetaValue>{formatNumber(report.crm_impact.unattributed_dialogs)}</MetaValue>
            </MetaRow>
            <MetaRow>
              <MetaLabel>{t('common:analyticsPage.ai.crm.riskyAmount')}</MetaLabel>
              <MetaValue>
                {formatCurrencyValue(
                  report.crm_impact.risky_deal_amount.amount,
                  report.crm_impact.risky_deal_amount.currency
                )}
              </MetaValue>
            </MetaRow>
            <MetaRow>
              <MetaLabel>{t('common:analyticsPage.ai.crm.lostAmount')}</MetaLabel>
              <MetaValue>
                {formatCurrencyValue(
                  report.crm_impact.lost_deal_amount.amount,
                  report.crm_impact.lost_deal_amount.currency
                )}
              </MetaValue>
            </MetaRow>
            <MetaRow>
              <MetaLabel>{t('common:analyticsPage.ai.crm.savedAmount')}</MetaLabel>
              <MetaValue>
                {formatCurrencyValue(
                  report.crm_impact.saved_deal_amount.amount,
                  report.crm_impact.saved_deal_amount.currency
                )}
              </MetaValue>
            </MetaRow>
          </InlineMetaList>
        </Panel>
      </SectionGrid>

      <Panel>
        <PanelHeader>
          <CustomTypography component="h2" variant="h6">
            {t('common:analyticsPage.ai.sections.aiActivity')}
          </CustomTypography>
        </PanelHeader>
        {renderMetricGrid(activityMetrics)}
      </Panel>

      <SectionGrid>
        <Panel>
          <PanelHeader>
            <CustomTypography component="h2" variant="h6">
              {t('common:analyticsPage.ai.sections.deliveryHealth')}
            </CustomTypography>
          </PanelHeader>
          {renderMetricGrid(deliveryMetrics)}
        </Panel>

        <Panel>
          <PanelHeader>
            <CustomTypography component="h2" variant="h6">
              {t('common:analyticsPage.ai.sections.safeAutomation')}
            </CustomTypography>
          </PanelHeader>
          <InlineMetaList>
            <MetaRow>
              <MetaLabel>{t('common:analyticsPage.ai.automation.allowed')}</MetaLabel>
              <MetaValue>{formatNumber(report.automation.allowed)}</MetaValue>
            </MetaRow>
            <MetaRow>
              <MetaLabel>{t('common:analyticsPage.ai.automation.blocked')}</MetaLabel>
              <MetaValue>{formatNumber(report.automation.blocked)}</MetaValue>
            </MetaRow>
            <MetaRow>
              <MetaLabel>{t('common:analyticsPage.ai.automation.dryRun')}</MetaLabel>
              <MetaValue>{formatNumber(report.automation.dry_run)}</MetaValue>
            </MetaRow>
            <MetaRow>
              <MetaLabel>{t('common:analyticsPage.ai.automation.delayRequired')}</MetaLabel>
              <MetaValue>
                {formatNumber(report.automation.anti_spam_queue_health.delay_required)}
              </MetaValue>
            </MetaRow>
            <MetaRow>
              <MetaLabel>{t('common:analyticsPage.ai.automation.dailyLimit')}</MetaLabel>
              <MetaValue>
                {formatNumber(
                  report.automation.anti_spam_queue_health.daily_limit_reached
                )}
              </MetaValue>
            </MetaRow>
            <MetaRow>
              <MetaLabel>{t('common:analyticsPage.ai.automation.queueFailed')}</MetaLabel>
              <MetaValue>
                {formatNumber(report.automation.anti_spam_queue_health.failed)}
              </MetaValue>
            </MetaRow>
            <MetaRow>
              <MetaLabel>{t('common:analyticsPage.ai.automation.retryPressure')}</MetaLabel>
              <MetaValue>
                {formatNumber(
                  report.automation.anti_spam_queue_health.retry_pressure
                )}
              </MetaValue>
            </MetaRow>
          </InlineMetaList>
        </Panel>
      </SectionGrid>

      <Panel>
        <PanelHeader>
          <CustomTypography component="h2" variant="h6">
            {t('common:analyticsPage.ai.sections.provider')}
          </CustomTypography>
        </PanelHeader>
        {renderMetricGrid(providerMetrics)}

        {report.provider.by_model.length > 0 ? (
          <TableScroller>
            <DataTable>
              <thead>
                <tr>
                  <th>{t('common:analyticsPage.ai.table.model')}</th>
                  <th>{t('common:analyticsPage.ai.table.calls')}</th>
                  <th>{t('common:analyticsPage.ai.table.failures')}</th>
                  <th>{t('common:analyticsPage.ai.table.avgLatency')}</th>
                  <th>{t('common:analyticsPage.ai.table.tokens')}</th>
                  <th>{t('common:analyticsPage.ai.table.cost')}</th>
                </tr>
              </thead>
              <tbody>
                {report.provider.by_model.map((model) => (
                  <tr key={`${model.provider}:${model.model}`}>
                    <td>
                      {model.provider} / {model.model}
                    </td>
                    <td>{formatNumber(model.calls)}</td>
                    <td>{formatNumber(model.failures)}</td>
                    <td>{formatDurationMs(model.avg_latency_ms)}</td>
                    <td>{formatNumber(model.total_tokens)}</td>
                    <td>{formatCurrencyValue(model.estimated_cost_usd, 'USD')}</td>
                  </tr>
                ))}
              </tbody>
            </DataTable>
          </TableScroller>
        ) : null}
      </Panel>

      <Panel>
        <PanelHeader>
          <CustomTypography component="h2" variant="h6">
            {t('common:analyticsPage.ai.sections.operational')}
          </CustomTypography>
        </PanelHeader>
        {renderMetricGrid(operationalMetrics)}
      </Panel>

      <SectionGrid>
        <Panel>
          <PanelHeader>
            <CustomTypography component="h2" variant="h6">
              {t('common:analyticsPage.ai.sections.risk')}
            </CustomTypography>
          </PanelHeader>
          <DenseMetricGrid>
            {(['low', 'medium', 'high', 'critical'] as const).map((level) => (
              <CompactMetricCard key={level}>
                <CompactMetricLabel variant="body2">
                  {t(`common:analyticsPage.ai.risk.${level}`)}
                </CompactMetricLabel>
                <CompactMetricValue component="div" variant="h6">
                  {formatNumber(report.risk[level])}
                </CompactMetricValue>
              </CompactMetricCard>
            ))}
          </DenseMetricGrid>
        </Panel>

        <Panel>
          <PanelHeader>
            <CustomTypography component="h2" variant="h6">
              {t('common:analyticsPage.ai.sections.channelHealth')}
            </CustomTypography>
          </PanelHeader>
          {report.channels.length > 0 ? (
            <ListGrid>
              {report.channels.map((channel) => {
                const hasIssue = channel.auth_issue || channel.billing_issue;

                return (
                  <ListItem key={channel.channel_id}>
                    <CustomTypography variant="body1">
                      {channel.name ?? channel.channel_id}
                    </CustomTypography>
                    <PanelHeaderMeta variant="body2">
                      {channel.protocol ?? '—'} / {channel.status ?? '—'} /{' '}
                      {t('common:analyticsPage.ai.channels.outbound', {
                        count: channel.outbound_messages,
                      })}
                    </PanelHeaderMeta>
                    <StatusPill tone={hasIssue ? 'partial' : 'ready'}>
                      {hasIssue
                        ? t('common:analyticsPage.ai.channels.hasIssue')
                        : t('common:analyticsPage.ai.channels.ready')}
                    </StatusPill>
                    {channel.last_error ? (
                      <PanelHeaderMeta variant="body2">
                        {channel.last_error}
                      </PanelHeaderMeta>
                    ) : null}
                  </ListItem>
                );
              })}
            </ListGrid>
          ) : (
            <EmptyText variant="body1">
              {t('common:analyticsPage.ai.state.empty')}
            </EmptyText>
          )}
        </Panel>
      </SectionGrid>

      <SectionGrid>
        <Panel>
          <PanelHeader>
            <CustomTypography component="h2" variant="h6">
              {t('common:analyticsPage.ai.sections.riskyDialogs')}
            </CustomTypography>
          </PanelHeader>
          {report.risk.top_risky_dialogs.length > 0 ? (
            <TableScroller>
              <DataTable>
                <thead>
                  <tr>
                    <th>{t('common:analyticsPage.ai.table.dialog')}</th>
                    <th>{t('common:analyticsPage.ai.table.risk')}</th>
                    <th>{t('common:analyticsPage.ai.table.reason')}</th>
                    <th>{t('common:analyticsPage.ai.table.crmDeal')}</th>
                    <th>{t('common:analyticsPage.ai.table.updated')}</th>
                  </tr>
                </thead>
                <tbody>
                  {report.risk.top_risky_dialogs.slice(0, 5).map((dialog) => (
                    <tr key={`${dialog.channel_id}:${dialog.chat_id}`}>
                      <td>
                        {dialog.channel_id}
                        <br />
                        {dialog.chat_id}
                      </td>
                      <td>
                        {t(`common:analyticsPage.ai.risk.${dialog.level}`)} /{' '}
                        {formatNumber(dialog.score)}
                      </td>
                      <td>{dialog.reason}</td>
                      <td>
                        {dialog.crm_deal_id ?? '—'}
                        <br />
                        {formatCurrencyValue(dialog.crm_deal_amount)}
                      </td>
                      <td>{formatDateTime(dialog.updated_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </DataTable>
            </TableScroller>
          ) : (
            <EmptyText variant="body1">
              {t('common:analyticsPage.ai.state.empty')}
            </EmptyText>
          )}
        </Panel>

        <Panel>
          <PanelHeader>
            <CustomTypography component="h2" variant="h6">
              {t('common:analyticsPage.ai.sections.lostLeads')}
            </CustomTypography>
            <PanelHeaderMeta variant="body2">
              {t('common:analyticsPage.ai.hints.joinedMetric')}
            </PanelHeaderMeta>
          </PanelHeader>
          {report.lost_leads.length > 0 ? (
            <TableScroller>
              <DataTable>
                <thead>
                  <tr>
                    <th>{t('common:analyticsPage.ai.table.dialog')}</th>
                    <th>{t('common:analyticsPage.ai.table.firstInbound')}</th>
                    <th>{t('common:analyticsPage.ai.table.firstResponse')}</th>
                    <th>{t('common:analyticsPage.ai.table.minutes')}</th>
                    <th>{t('common:analyticsPage.ai.table.crmDeal')}</th>
                  </tr>
                </thead>
                <tbody>
                  {report.lost_leads.slice(0, 5).map((lead) => (
                    <tr key={`${lead.channel_id}:${lead.chat_id}`}>
                      <td>
                        {lead.channel_id}
                        <br />
                        {lead.chat_id}
                      </td>
                      <td>{formatDateTime(lead.first_inbound_at)}</td>
                      <td>
                        {lead.first_response_at
                          ? formatDateTime(lead.first_response_at)
                          : '—'}
                      </td>
                      <td>{formatMetricValue(lead.minutes_to_response)}</td>
                      <td>
                        {lead.crm_deal_id ?? '—'}
                        <br />
                        {formatCurrencyValue(lead.crm_deal_amount)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </DataTable>
            </TableScroller>
          ) : (
            <EmptyText variant="body1">
              {t('common:analyticsPage.ai.state.empty')}
            </EmptyText>
          )}
        </Panel>
      </SectionGrid>

      <SectionGrid>
        <Panel>
          <PanelHeader>
            <CustomTypography component="h2" variant="h6">
              {t('common:analyticsPage.ai.sections.automationSources')}
            </CustomTypography>
          </PanelHeader>
          {report.automation.by_source.length > 0 ? (
            <ListGrid>
              {report.automation.by_source.map((source) => (
                <ListItem key={source.source}>
                  <CustomTypography variant="body1">
                    {translateDynamic(
                      t,
                      `common:analyticsPage.ai.sources.${source.source}`,
                      formatCode(source.source)
                    )}
                  </CustomTypography>
                  <PanelHeaderMeta variant="body2">
                    {t('common:analyticsPage.ai.automation.sourceSummary', {
                      allowed: source.allowed,
                      blocked: source.blocked,
                      dryRun: source.dry_run,
                    })}
                  </PanelHeaderMeta>
                </ListItem>
              ))}
            </ListGrid>
          ) : (
            <EmptyText variant="body1">
              {t('common:analyticsPage.ai.state.empty')}
            </EmptyText>
          )}
        </Panel>

        <Panel>
          <PanelHeader>
            <CustomTypography component="h2" variant="h6">
              {t('common:analyticsPage.ai.sections.blockReasons')}
            </CustomTypography>
          </PanelHeader>
          {report.automation.by_reason.length > 0 ? (
            <ListGrid>
              {report.automation.by_reason.map((reason) => (
                <ListItem key={reason.reason}>
                  <CustomTypography variant="body1">
                    {translateDynamic(
                      t,
                      `common:analyticsPage.ai.reasons.${reason.reason}`,
                      formatCode(reason.reason)
                    )}
                  </CustomTypography>
                  <PanelHeaderMeta variant="body2">
                    {t('common:analyticsPage.ai.automation.reasonCount', {
                      count: reason.count,
                    })}
                  </PanelHeaderMeta>
                </ListItem>
              ))}
            </ListGrid>
          ) : (
            <EmptyText variant="body1">
              {t('common:analyticsPage.ai.state.empty')}
            </EmptyText>
          )}
        </Panel>
      </SectionGrid>
    </>
  );
};
